import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import ComplianceAIForm from '@/components/compliance-ai/ComplianceAIForm';
import ComplianceAIResponse from '@/components/compliance-ai/ComplianceAIResponse';
import { ComplianceFormData } from '@/types/compliance';
import { mockComplianceAiResponse } from '@/mocks/complianceAiResponse';
import { API_CONFIG } from '@/constants/env.ts';
import { GlowBackground } from '@/components/GlowBackground';
import LoadingAnimation from '@/components/ui/loading-animation';
import { CTA } from '@/components/cta';
import {
  ArrowRight,
  BarChart3,
  Brain,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileCheck,
  Play,
  Shield,
} from 'lucide-react';

// Image Slider Component
interface ImageSliderProps {
  images: Array<{
    src: string;
    alt: string;
  }>;
  interval?: number;
}

const ImageSlider: React.FC<ImageSliderProps> = ({ images, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      if (!isPaused) {
        setCurrentIndex(prevIndex => (prevIndex + 1) % images.length);
      }
    }, interval);
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, images.length, interval]);

  return (
    <div
      className="rounded-xl overflow-hidden relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            className="w-full h-auto"
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full ${
              index === currentIndex ? 'bg-green-500' : 'bg-gray-300'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Wilson-style Landing Page for Financial Marketing Compliance
const FinanceMarketingWilsonLanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(90); // 90 second countdown

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);
    setTimeRemaining(90); // Reset timer to 90 seconds

    try {
      // Use the actual API endpoint from API_CONFIG
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log('Sending request to compliance AI API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`
        );
      }

      let responseData = await response.json();

      console.log('API Response:', responseData);

      // Handle different response formats
      if (typeof responseData === 'string') {
        try {
          // Try to parse if it's a JSON string
          responseData = JSON.parse(responseData);
        } catch (e) {
          // If it's not valid JSON, wrap it in a content property
          responseData = { content: responseData };
        }
      }

      // Handle direct response object format (post-processed by backend)
      if (
        responseData.text !== undefined &&
        responseData.advice !== undefined &&
        responseData.diagram !== undefined
      ) {
        console.log('Got well-formed response from API with text/advice/diagram properties');

        // Fix potential diagram issues if it's a string (for backward compatibility)
        if (typeof responseData.diagram === 'string') {
          fixDiagramSyntax(responseData);
        }
      }
      // Handle nested content property
      else if (responseData.content && typeof responseData.content === 'string') {
        try {
          const parsed = JSON.parse(responseData.content);
          if (parsed.text || parsed.advice || parsed.diagram) {
            responseData = parsed;
            if (typeof responseData.diagram === 'string') {
              fixDiagramSyntax(responseData);
            }
          }
        } catch (e) {
          console.error('Failed to parse content string:', e);
        }
      }
      // Handle Python dict-like representation
      else if (
        typeof responseData === 'object' &&
        Object.keys(responseData).some(key => key.includes("'"))
      ) {
        console.log('Got Python dict-like response, converting to proper JSON');
        try {
          // Convert Python dict format to proper JSON object
          const fixedData = {};

          Object.keys(responseData).forEach(key => {
            // Remove single quotes and fix key names
            const cleanKey = key.replace(/'/g, '');
            fixedData[cleanKey] = responseData[key];
          });

          // Set the properly formatted data
          responseData = fixedData;

          // Fix potential diagram issues if it's a string
          if (typeof responseData.diagram === 'string') {
            fixDiagramSyntax(responseData);
          }

          console.log('Converted response:', responseData);
        } catch (e) {
          console.error('Failed to convert Python dict format:', e);
        }
      }

      console.log('Final processed response for component:', responseData);

      // Ensure we show the loading animation for at least 5 seconds
      const elapsedTime = 90 - timeRemaining;
      if (elapsedTime < 5) {
        // Wait until we've shown the animation for at least 5 seconds
        setTimeout(
          () => {
            setResponse(responseData);
            setIsLoading(false);
          },
          (5 - elapsedTime) * 1000
        );
      } else {
        setResponse(responseData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }

      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log('Falling back to mock data due to API error');
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: 'Financial Marketing Compliance Assessment',
            summary:
              'Your financial marketing materials have been analyzed for regulatory compliance. Here are our recommendations to improve your compliance posture.',
          });
        }, 1000);
      }
    }
  };

  // Helper function to fix diagram syntax issues
  function fixDiagramSyntax(dataObj) {
    if (!dataObj.diagram || typeof dataObj.diagram !== 'string') return;

    // Special case for the exact diagram format in the console log
    if (dataObj.diagram.includes('style F1 fill:#90EE90') && !dataObj.diagram.endsWith('end')) {
      console.log('Detected the exact diagram format from the console log');
      dataObj.diagram += '\n    end';
      console.log('Fixed the diagram by adding missing end tag');
      return;
    }

    // Count subgraph and end tags
    const subgraphCount = (dataObj.diagram.match(/subgraph/g) || []).length;
    const endCount = (dataObj.diagram.match(/end/g) || []).length;

    console.log(`Diagram analysis - Subgraph count: ${subgraphCount}, End count: ${endCount}`);

    // Add missing end tags
    if (subgraphCount > endCount) {
      for (let i = 0; i < subgraphCount - endCount; i++) {
        dataObj.diagram += ' end';
      }
    }

    // For diagrams with style declarations at the end
    if (dataObj.diagram.match(/style\s+[A-Z0-9]+\s+fill:#[0-9A-F]{6}$/i)) {
      console.log('Diagram ends with a style declaration, adding end tag');
      dataObj.diagram += '\n    end';
    }

    // Log the fixed diagram
    console.log('Fixed diagram:', dataObj.diagram);
  }

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Regulatory frameworks covered
  const regulations = [
    { name: 'AIFMD', description: 'Alternative Investment Fund Managers Directive' },
    { name: 'MiFID II', description: 'Markets in Financial Instruments Directive' },
    { name: 'FINRA', description: 'Financial Industry Regulatory Authority' },
    { name: 'SEC', description: 'Securities and Exchange Commission' },
    { name: 'FCA', description: 'Financial Conduct Authority' },
    { name: 'ESMA', description: 'European Securities and Markets Authority' },
    { name: 'GDPR', description: 'General Data Protection Regulation' },
    { name: 'CCPA/CPRA', description: 'California Consumer Privacy Act' },
  ];

  // Key features cards
  const keyFeatures = [
    {
      title: 'Automated Pre-Approval',
      description:
        'Our AI analyzes financial marketing content in seconds, identifying compliance issues and suggesting fixes before materials go to review.',
      icon: <Brain className="h-10 w-10 text-green-500" />,
    },
    {
      title: 'Risk Detection',
      description:
        'Identify potential compliance risks across all marketing channels, from web content to social media and promotional materials.',
      icon: <Shield className="h-10 w-10 text-green-500" />,
    },
    {
      title: 'Streamlined Workflow',
      description:
        'Reduce review times by up to 90%, allowing your marketing team to move quickly while maintaining regulatory compliance.',
      icon: <BarChart3 className="h-10 w-10 text-green-500" />,
    },
  ];

  // Benefits
  const benefits = [
    {
      title: '90% Faster Compliance Reviews',
      description:
        'Reduce review times from days to minutes with AI-powered content analysis that identifies issues before they reach compliance teams.',
      icon: <Clock className="h-6 w-6 text-green-500" />,
    },
    {
      title: 'Reduced Compliance Risk',
      description:
        'Our AI identifies subtle regulatory issues that human reviewers might miss, helping you avoid costly compliance violations.',
      icon: <Shield className="h-6 w-6 text-green-500" />,
    },
    {
      title: 'Multi-Channel Coverage',
      description:
        'Monitor compliance across all marketing channels - websites, social media, email campaigns, and promotional materials.',
      icon: <FileCheck className="h-6 w-6 text-green-500" />,
    },
    {
      title: 'Industry-Specific Knowledge',
      description:
        'Our AI is trained on financial regulations and industry best practices to provide tailored compliance guidance.',
      icon: <Briefcase className="h-6 w-6 text-green-500" />,
    },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How accurate is the AI compliance analysis?',
      answer:
        'Our AI achieves a 90% accuracy rate for financial marketing compliance analysis, comparable to expert human reviewers but delivered in a fraction of the time.',
    },
    {
      question: 'What regulatory frameworks does your platform cover?',
      answer:
        'Our platform covers major financial regulations including AIFMD, MiFID II, FINRA, SEC requirements, FCA guidelines, ESMA directives, and more. We continuously update our AI with the latest regulatory changes.',
    },
    {
      question: 'How does the platform integrate with our existing workflows?',
      answer:
        'Verdea AI offers seamless integration with popular marketing tools, CMS platforms, and compliance management systems. Our API allows for custom integrations with your existing tech stack.',
    },
    {
      question: 'How do you handle data security and privacy?',
      answer:
        'We maintain the highest standards of data security with GDPR compliance, end-to-end encryption, and secure cloud infrastructure. Your sensitive financial information never leaves our secure environment.',
    },
  ];

  // Testimonials from financial institutions
  const testimonials = [
    {
      quote:
        'Verdea has reduced our compliance review cycle from 5 days to just hours, enabling our marketing team to be more agile without sacrificing regulatory compliance.',
      author: 'Sarah Johnson',
      title: 'Chief Compliance Officer',
      company: 'Global Investment Partners',
    },
    {
      quote:
        "The platform's ability to spot potential compliance issues before content even reaches our review team has been transformative for our workflow efficiency.",
      author: 'Michael Chen',
      title: 'Head of Marketing',
      company: 'Meridian Financial Services',
    },
    {
      quote:
        'Implementing Verdea has reduced our compliance-related marketing delays by 85% while improving our regulatory adherence across multiple jurisdictions.',
      author: 'Elizabeth Torres',
      title: 'VP of Regulatory Affairs',
      company: 'Atlantic Trust Bank',
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>AI-Powered Financial Marketing Compliance | Verdea AI</title>
        <meta
          name="description"
          content="Transform your financial marketing compliance with AI. Streamline pre-approval reviews and ensure regulatory compliance across all marketing channels."
        />
      </Helmet>

      <GlowBackground colorScheme={'green'} />

      {/* Navbar - only shown when not loading */}
      {!isLoading && (
        <header
          className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}
        >
          <NavBar landingMode={true} />
        </header>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="bg-card p-8 rounded-2xl shadow-xl border border-border">
                <LoadingAnimation
                  timeRemaining={timeRemaining}
                  setTimeRemaining={setTimeRemaining}
                  initialTime={90}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <main>
        {/* Hero Section - Wilson Style */}
        <section className="relative pt-32 pb-40 overflow-hidden bg-gradient-to-b from-green-50 to-white">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 -right-10 w-[600px] h-[600px] opacity-20 rounded-full bg-green-300/30 blur-3xl" />
            <div className="absolute bottom-0 -left-10 w-[600px] h-[600px] opacity-10 rounded-full bg-green-300/30 blur-3xl" />

            {/* Dots pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0iIzAwMCIgZmlsbC1vcGFjaXR5PSIuMDciLz48L3N2Zz4=')] opacity-40" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left column - Text content */}
              <div className="max-w-2xl">
                <div className="flex items-center mb-6 space-x-2">
                  <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                    NEW
                  </span>
                  <span className="text-sm text-gray-600">Modular AI Regulatory Tech Platform</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
                  <span className="block">Financial Marketing</span>
                  <span className="bg-gradient-to-r from-[#40ffaa] to-[#184c06] bg-clip-text text-transparent">
                    Compliance, Reimagined
                  </span>
                </h1>

                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Streamline compliance reviews, accelerate time-to-market, and mitigate regulatory
                  risks with our modular AI regulatory technology platform for financial
                  institutions.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <Button
                    size="lg"
                    className="bg-neutral-900 hover:bg-black/90 text-white px-6 py-2.5 text-base shadow-lg hover:scale-105 flex items-center gap-2"
                    onClick={() => {
                      document
                        .getElementById('compliance-form')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span>Try Free Analysis</span>
                    <ArrowRight className="h-5 w-5" />
                  </Button>


                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-green-600">90%</span>
                    <span className="text-sm text-gray-600">Faster Reviews</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-green-600">90%</span>
                    <span className="text-sm text-gray-600">Accuracy</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-green-600">8+</span>
                    <span className="text-sm text-gray-600">Regulations</span>
                  </div>
                </div>
              </div>

              {/* Right column - Dashboard preview */}
              <div className="rounded-2xl shadow-2xl shadow-green-200 relative bg-white p-2">
                <ImageSlider
                  images={[
                    { src: '/screens/dash2.png', alt: 'Financial Marketing Compliance Dashboard' },
                    { src: '/screens/dash1.png', alt: 'Financial Marketing Analytics View' },
                    { src: '/screens/dashboard_overview.png', alt: 'Dashboard Overview' },
                  ]}
                />

                {/* Floating stats cards */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 max-w-[200px]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Compliance Rate</p>
                      <p className="text-xl font-bold text-green-600">98.2%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Logos Section */}
        <section className="py-12 border-y border-gray-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-gray-600 text-sm font-medium mb-4">
                TRUSTED BY LEADING FINANCIAL INSTITUTIONS
              </p>
              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
                <img
                  src="/logos/vba.png"
                  alt="VBA"
                  className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                />
                <img
                  src="/logos/ant.png"
                  alt="Ant Financial"
                  className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                />
                <img
                  src="/logos/mm1.png"
                  alt="MM1"
                  className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                />
                <img
                  src="/logos/ejaso.png"
                  alt="Ejaso"
                  className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                />
                <img
                  src="/logos/greentech.jpg"
                  alt="GreenTech"
                  className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section - Wilson Style */}
        <section id="how-it-works" className="py-24 bg-white relative">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                How Our AI Compliance Platform Works
              </h2>
              <p className="text-xl text-gray-600">
                Our platform transforms how financial institutions handle marketing compliance,
                saving time while reducing risk.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  title: 'Upload Marketing Content',
                  description:
                    'Submit your marketing materials through our secure platform - websites, ads, social media, or brochures.',
                  icon: <FileCheck className="h-10 w-10 text-indigo-500" />,
                  step: '01',
                },
                {
                  title: 'AI-Powered Analysis',
                  description:
                    'Our specialized AI reviews content against relevant regulatory frameworks and compliance standards.',
                  icon: <Brain className="h-10 w-10 text-indigo-500" />,
                  step: '02',
                },
                {
                  title: 'Receive Detailed Report',
                  description:
                    'Get actionable recommendations, risk assessments, and suggested revisions to ensure compliance.',
                  icon: <BarChart3 className="h-10 w-10 text-indigo-500" />,
                  step: '03',
                },
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-xl shadow-green-100 h-full flex flex-col">
                    <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div className="mb-6">
                      {React.cloneElement(step.icon, { className: 'h-10 w-10 text-green-500' })}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Demo Form Section */}
        <section id="compliance-form" className="py-24 bg-gray-50 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-20 rounded-full bg-indigo-300/30 blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full mb-4">
                    FREE ANALYSIS
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                    Try Our Financial Marketing Compliance AI
                  </h2>
                  <p className="text-xl text-gray-600 mb-6">
                    Get a free compliance analysis of your financial marketing content. No credit
                    card required.
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      'AI-powered compliance check against major financial regulations',
                      'Detailed report with compliance gaps and recommendations',
                      'Risk assessment based on regulatory frameworks',
                      'Actionable suggestions to improve compliance',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  {!response ? (
                    <>
                      <h3 className="text-2xl font-bold mb-6">
                        Tell us about your marketing compliance challenges
                      </h3>
                      <ComplianceAIForm
                        onSubmit={handleFormSubmit}
                        isLoading={isLoading}
                        error={error}
                      />
                    </>
                  ) : (
                    <ComplianceAIResponse
                      response={response}
                      isLoading={isLoading}
                      error={error}
                      onReset={() => {
                        setFormData(null);
                        setResponse(null);
                        setError(null);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section - Wilson Style */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Key Features</h2>
              <p className="text-xl text-gray-600">
                Our platform transforms how financial institutions handle marketing compliance,
                saving time while reducing risk.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg"
                >
                  <div className="mb-6">
                    {React.cloneElement(feature.icon, { className: 'h-10 w-10 text-green-500' })}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Regulatory Frameworks Section */}
        <section className="py-24 bg-gradient-to-b from-green-50 to-white relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] opacity-20 rounded-full bg-green-300/30 blur-3xl" />

            {/* Dots pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0iIzAwMCIgZmlsbC1vcGFjaXR5PSIuMDciLz48L3N2Zz4=')] opacity-40" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Comprehensive Regulatory Coverage
              </h2>
              <p className="text-xl text-gray-600">
                Our platform helps financial institutions maintain compliance across multiple
                regulatory frameworks and jurisdictions.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {regulations.map((regulation, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl border border-green-100 shadow-lg shadow-green-100/20"
                >
                  <h3 className="font-bold text-green-700 mb-2">{regulation.name}</h3>
                  <p className="text-sm text-gray-600">{regulation.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section - Wilson Style */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Key Benefits</h2>
              <p className="text-xl text-gray-600">
                Why financial institutions choose our AI-powered marketing compliance platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl flex items-start gap-5 shadow-lg"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    {React.cloneElement(benefit.icon, { className: 'h-6 w-6 text-green-500' })}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - Wilson Style */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">
                Common questions about our financial marketing compliance platform
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="space-y-6">
                {faqItems.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <details className="group">
                      <summary className="flex justify-between items-center p-6 cursor-pointer">
                        <h3 className="text-lg font-medium">{faq.question}</h3>
                        <div className="ml-4 flex-shrink-0 transition-transform duration-300 group-open:rotate-180">
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        </div>
                      </summary>
                      <div className="px-6 pb-6 text-gray-600">
                        <p>{faq.answer}</p>
                      </div>
                    </details>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Homepage-style CTA Section */}
      <CTA />

      {!isLoading && (
        <footer>
          <Footer landingMode={true} />
        </footer>
      )}
    </div>
  );
};

export default FinanceMarketingWilsonLanding;
